import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { BeatLoader } from 'react-spinners'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

const displayValue = (value) => (value?.trim?.() ? value : '—')

const getErrorMessage = (err, fallback) => {
    const data = err.response?.data
    if (!data) return fallback
    if (typeof data === 'string') return data
    if (data.detail) return data.detail
    if (data.message) return data.message
    return Object.entries(data)
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join(' ')
}

const getClientName = (client) => {
    if (!client) return '—'
    if (typeof client === 'string') return client
    const name = [client.first_name, client.last_name].filter(Boolean).join(' ')
    if (name) return name
    if (client.company?.name) return client.company.name
    if (client.name) return client.name
    return '—'
}

const formatDate = (dateStr) => {
    if (!dateStr) return '—'
    const date = new Date(dateStr)
    return Number.isNaN(date.getTime())
        ? dateStr
        : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const formatAmount = (amount, currency) => {
    if (amount == null || amount === '') return '—'
    const num = Number(amount)
    const formatted = Number.isNaN(num)
        ? amount
        : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    if (currency === 'USD') return `$${formatted}`
    if (currency === 'ZIG') return `ZIG ${formatted}`
    return formatted
}

const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase?.()) {
        case 'paid':
            return 'badge-soft-success'
        case 'draft':
            return 'badge-soft-warning'
        case 'sent':
            return 'badge-soft-primary'
        case 'cancelled':
            return 'badge-soft-danger'
        default:
            return 'badge-soft-secondary'
    }
}

const formatStatusLabel = (status) => {
    if (!status) return '—'
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const Invoices = () => {
    const [invoices, setInvoices] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [expandedId, setExpandedId] = useState(null)

    const fetchInvoices = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const response = await api.get('/invoices/')
            setInvoices(Array.isArray(response.data) ? response.data : [])
        } catch (err) {
            console.error('Failed to fetch invoices', err)
            setError(getErrorMessage(err, 'Failed to load invoices. Please try again.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        fetchInvoices()
    }, [fetchInvoices])

    const toggleDetails = (invoiceId) => {
        setExpandedId((current) => (current === invoiceId ? null : invoiceId))
    }

    const generatePDF = async (invoice) => {
        try {
            const companyRes = await api.get('/auth/company/')
            const company = companyRes.data
            
            const printWindow = window.open('', '_blank', 'width=800,height=900')
            if (!printWindow) {
                alert('Please allow popups to generate PDF')
                return
            }

            const client = invoice.client_details || {}
            const clientName = getClientName(invoice.client_name)
            const lineItems = invoice.line_items || []

            const logoUrl = company.logo 
                ? (company.logo.startsWith('http') ? company.logo : `http://127.0.0.1:8000${company.logo}`)
                : ''

            printWindow.document.write(`
                <html>
                <head>
                    <title>Invoice ${invoice.invoice_number}</title>
                    <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
                    <style>
                        body { font-family: 'Inter', sans-serif; color: #333; padding: 40px; }
                        .invoice-header { border-bottom: 2px solid #eee; padding-bottom: 20px; margin-bottom: 30px; }
                        .company-logo { max-height: 70px; object-fit: contain; }
                        .invoice-title { font-size: 28px; font-weight: 700; color: #1e293b; }
                        .table th { background-color: #f8fafc; font-weight: 600; }
                        .total-box { background-color: #f8fafc; border-radius: 6px; padding: 15px; }
                        @media print {
                            body { padding: 0; }
                            .no-print { display: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="container">
                        <div class="invoice-header d-flex justify-content-between align-items-center">
                            <div>
                                ${logoUrl ? `<img src="http:/${company.logo}" class="company-logo mb-2" alt="Company Logo" />` : ''}
                                <h4 class="fw-bold m-0">${company.name || 'Our Company'}</h4>
                                <p class="text-muted m-0 fs-13">${company.email || ''} | ${company.phone || ''}</p>
                                <p class="text-muted m-0 fs-13">${company.address || ''}</p>
                            </div>
                            <div class="text-end">
                                <span class="invoice-title">INVOICE</span>
                                <p class="m-0 fw-semibold text-primary">#${invoice.invoice_number}</p>
                                <p class="text-muted m-0 fs-13">Date: ${formatDate(invoice.invoice_date)}</p>
                                <p class="text-muted m-0 fs-13">Due Date: ${formatDate(invoice.due_date)}</p>
                            </div>
                        </div>

                        <div class="row mb-4">
                            <div class="col-6">
                                <h6 class="text-muted text-uppercase fs-12 fw-bold mb-2">Billed To</h6>
                                <h5 class="fw-bold m-0">${clientName}</h5>
                                <p class="text-muted m-0 fs-13">${client.email || ''}</p>
                                <p class="text-muted m-0 fs-13">${client.phone || ''}</p>
                                <p class="text-muted m-0 fs-13">${[client.address_line1, client.address_line2, client.city, client.country].filter(Boolean).join(', ')}</p>
                            </div>
                            <div class="col-6 text-end">
                                <h6 class="text-muted text-uppercase fs-12 fw-bold mb-2">Payment Details</h6>
                                <p class="m-0 fs-13"><strong>Currency:</strong> ${invoice.currency}</p>
                                <p class="m-0 fs-13"><strong>Status:</strong> ${invoice.status}</p>
                            </div>
                        </div>

                        <table class="table table-striped">
                            <thead>
                                <tr>
                                    <th>Item / Description</th>
                                    <th class="text-end">Quantity</th>
                                    <th class="text-end">Unit Price</th>
                                    <th class="text-end">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${lineItems.map(item => `
                                    <tr>
                                        <td>
                                            <div class="fw-semibold">${item.name}</div>
                                            ${item.description ? `<small class="text-muted">${item.description}</small>` : ''}
                                        </td>
                                        <td class="text-end">${item.quantity}</td>
                                        <td class="text-end">${formatAmount(item.unit_price, invoice.currency)}</td>
                                        <td class="text-end">${formatAmount(item.line_total || (item.quantity * item.unit_price), invoice.currency)}</td>
                                    </tr>
                                `).join('')}
                            </tbody>
                        </table>

                        <div class="row justify-content-end mt-4">
                            <div class="col-md-5">
                                <div class="total-box">
                                    <div class="d-flex justify-content-between mb-2">
                                        <span class="text-muted">Sub Total</span>
                                        <span class="fw-semibold">${formatAmount(invoice.subtotal, invoice.currency)}</span>
                                    </div>
                                    <div class="d-flex justify-content-between border-top pt-2">
                                        <span class="fw-bold">Grand Total</span>
                                        <span class="fw-bold text-primary fs-18">${formatAmount(invoice.grand_total, invoice.currency)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        ${invoice.payment_instructions ? `
                            <div class="mt-4">
                                <h6 class="text-muted text-uppercase fs-12 fw-bold mb-1">Payment Instructions</h6>
                                <p class="fs-13 text-muted">${invoice.payment_instructions}</p>
                            </div>
                        ` : ''}

                        ${invoice.notes ? `
                            <div class="mt-2">
                                <h6 class="text-muted text-uppercase fs-12 fw-bold mb-1">Notes</h6>
                                <p class="fs-13 text-muted">${invoice.notes}</p>
                            </div>
                        ` : ''}
                    </div>
                    <script>
                        window.onload = function() {
                            window.print();
                        }
                    </script>
                </body>
                </html>
            `)
            printWindow.document.close()
        } catch (err) {
            console.error(err)
            alert('Failed to generate invoice PDF')
        }
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />

            <div className="page-wrapper">
                <div className="content content-two pb-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                        <h5 className="d-flex align-items-center mb-0">
                            Invoices
                            {!loading && !error && (
                                <span className="badge bg-soft-dark ms-2 text-dark fs-12">
                                    {invoices.length} {invoices.length === 1 ? 'Invoice' : 'Invoices'}
                                </span>
                            )}
                        </h5>
                        <Link to="/create-invoice" className="btn btn-md btn-primary d-flex align-items-center">
                            <i className="ti ti-circle-plus me-2" />
                            Add Invoice
                        </Link>
                    </div>

                    <div className="card shadow-sm">
                        <div className="card-header d-flex align-items-center justify-content-between">
                            <h5 className="fw-bold mb-0">All Invoices</h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-white"
                                onClick={fetchInvoices}
                                disabled={loading}
                            >
                                <i className="ti ti-refresh me-1" />
                                Refresh
                            </button>
                        </div>

                        <div className="card-body p-0">
                            {loading && (
                                <div className="d-flex justify-content-center align-items-center py-5">
                                    <BeatLoader color="#3550DC" size={10} />
                                </div>
                            )}

                            {!loading && error && (
                                <div className="alert alert-danger m-3 mb-0" role="alert">
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                                        <span>
                                            <i className="ti ti-alert-circle me-2" />
                                            {error}
                                        </span>
                                        <button type="button" className="btn btn-sm btn-danger" onClick={fetchInvoices}>
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && invoices.length === 0 && (
                                <div className="text-center py-5">
                                    <div className="avatar avatar-lg bg-light text-muted mx-auto mb-3">
                                        <i className="ti ti-file-invoice fs-24" />
                                    </div>
                                    <h6 className="fw-semibold mb-1">No invoices found</h6>
                                    <p className="text-muted mb-3">Invoices you create will appear here.</p>
                                    <Link to="/create-invoice" className="btn btn-primary">
                                        <i className="ti ti-plus me-1" />
                                        Add Invoice
                                    </Link>
                                </div>
                            )}

                            {!loading && !error && invoices.length > 0 && (
                                <div className="table-responsive">
                                    <table className="table table-nowrap border mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Invoice #</th>
                                                <th>Client</th>
                                                <th>Date</th>
                                                <th>Currency</th>
                                                <th>Sub Total</th>
                                                <th>Grand Total</th>
                                                <th>Status</th>
                                                <th className="text-end">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map((invoice) => {
                                                const invoiceKey = invoice.id ?? invoice.invoice_number
                                                const isExpanded = expandedId === invoiceKey

                                                return (
                                                    <React.Fragment key={invoiceKey}>
                                                        <tr>
                                                            <td>
                                                                <span className="fw-medium text-dark">
                                                                    {displayValue(invoice.invoice_number)}
                                                                </span>
                                                            </td>
                                                            <td>{getClientName(invoice.client_name)}</td>
                                                            <td>{formatDate(invoice.invoice_date)}</td>
                                                            <td>{displayValue(invoice.currency)}</td>
                                                            <td>{formatAmount(invoice.subtotal, invoice.currency)}</td>
                                                            <td>{formatAmount(invoice.grand_total, invoice.currency)}</td>
                                                            <td>
                                                                <span className={`badge ${getStatusBadgeClass(invoice.status)}`}>
                                                                    {formatStatusLabel(invoice.status)}
                                                                </span>
                                                            </td>
                                                            <td className="text-end">
                                                                <div className="d-inline-flex align-items-center gap-1">
                                                                    <Link
                                                                        to={`/invoice-details/${invoice.id}`}
                                                                        className="btn btn-sm btn-outline-primary"
                                                                    >
                                                                        <i className="ti ti-eye me-1" />
                                                                        Details
                                                                    </Link>
                                                                    <Link
                                                                        to={`/edit-invoice/${invoice.id}`}
                                                                        className="btn btn-sm btn-outline-secondary"
                                                                    >
                                                                        <i className="ti ti-edit me-1" />
                                                                        Edit
                                                                    </Link>
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => generatePDF(invoice)}
                                                                    >
                                                                        <i className="ti ti-file-type-pdf me-1" />
                                                                        PDF
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>

                                                        {isExpanded && (
                                                            <tr className="bg-light">
                                                                <td colSpan={8} className="p-0">
                                                                    <div className="p-3 px-4 border-top">
                                                                        <div className="row g-3">
                                                                            <div className="col-md-6">
                                                                                <p className="text-muted fs-12 mb-1">Payment Instructions</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(invoice.payment_instructions)}</p>
                                                                            </div>
                                                                            <div className="col-md-6">
                                                                                <p className="text-muted fs-12 mb-1">Notes</p>
                                                                                <p className="mb-0 fw-medium">{displayValue(invoice.notes)}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default Invoices
